import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, Loader2, CheckCircle2, Gem } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import api from "@/lib/api";

/**
 * Public /unsubscribe?token=... page. No login required.
 *
 * Behavior:
 *   * On mount we call /marketing/unsubscribe/verify to obtain a
 *     masked email so the customer knows which address they're
 *     unsubscribing.
 *   * Pressing Unsubscribe posts the token to /marketing/unsubscribe.
 *   * The endpoint is idempotent — clicking twice is safe.
 *
 * We NEVER accept a raw email in the URL. Only the signed token.
 */
export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({
    loading: true,
    validToken: false,
    emailMasked: null,
    unsubscribed: false,
    submitting: false,
    error: null,
  });

  // Privacy hardening for the /unsubscribe route:
  //   * Add a page-level Referrer-Policy: no-referrer meta tag so
  //     outgoing links do not leak ?token=... via the Referer header.
  //   * Strip ?token=... from the visible URL bar immediately after
  //     we've read it into React state, so screenshots, browser
  //     history sync, and analytics page-view captures no longer see
  //     the raw token. The token stays in-memory in React state only.
  //   * PostHog auto-capture / session recording / pageview are ALREADY
  //     disabled at init in public/index.html when the initial path is
  //     /unsubscribe, so nothing analytics-related fires before this
  //     effect runs. We do NOT force PostHog back on when leaving the
  //     route \u2014 that would flip an analytics-declined user to opt-in.
  //     We only restore state we actually changed.
  useEffect(() => {
    // Referrer-Policy meta \u2014 injected only on this page
    let meta = document.querySelector('meta[name="referrer"]');
    const created = !meta;
    if (created) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "referrer");
      document.head.appendChild(meta);
    }
    const previous = meta.getAttribute("content") || "";
    meta.setAttribute("content", "no-referrer");

    // Scrub token from the URL bar so analytics/history do not
    // capture it. React state has already read it into `token`.
    try {
      if (window.location.search.includes("token=")) {
        window.history.replaceState({}, "", window.location.pathname);
      }
    } catch (_e) {
      /* ignore \u2014 non-fatal */
    }

    return () => {
      // Restore referrer policy when leaving the page.
      if (created && meta && meta.parentNode) {
        meta.parentNode.removeChild(meta);
      } else if (meta) {
        if (previous) meta.setAttribute("content", previous);
        else meta.removeAttribute("content");
      }
      // DELIBERATELY do NOT call posthog.opt_in_capturing() here.
      // PostHog was never opted out by this component (the suppression
      // for /unsubscribe happens at init time in public/index.html
      // by disabling autocapture/pageview/session_recording for this
      // single route). Calling opt_in_capturing() would convert an
      // analytics-declined user into an analytics-opted-in user, which
      // violates their prior consent choice.
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        if (alive) setState((s) => ({ ...s, loading: false, validToken: false }));
        return;
      }
      try {
        const res = await api.get(`/marketing/unsubscribe/verify`, { params: { token } });
        if (!alive) return;
        setState((s) => ({
          ...s,
          loading: false,
          validToken: Boolean(res?.data?.valid),
          emailMasked: res?.data?.email_masked || null,
        }));
      } catch (_e) {
        if (!alive) return;
        setState((s) => ({ ...s, loading: false, validToken: false }));
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const doUnsubscribe = async () => {
    setState((s) => ({ ...s, submitting: true, error: null }));
    try {
      await api.post("/marketing/unsubscribe", { token });
      setState((s) => ({ ...s, submitting: false, unsubscribed: true }));
    } catch (err) {
      setState((s) => ({
        ...s,
        submitting: false,
        error: err?.response?.data?.detail || "Could not unsubscribe right now.",
      }));
    }
  };

  return (
    <>
      <SEO
        title="Unsubscribe | Postmoro"
        description="Unsubscribe from Postmoro marketing and product-update emails. Transactional emails are unaffected."
        path="/unsubscribe"
        noindex
      />
      <div
        className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] flex flex-col"
        data-testid="unsubscribe-page"
      >
        <header className="border-b border-[color:var(--border-subtle)]">
          <div className="max-w-[720px] mx-auto px-4 sm:px-6 h-16 flex items-center">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] shadow-[0_4px_18px_rgba(27,168,159,0.45)]">
                <Gem className="h-4 w-4" />
              </div>
              <span className="font-display text-lg font-semibold tracking-tight">Postmoro</span>
            </Link>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div
            className="w-full max-w-[520px] rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6 sm:p-8"
            data-testid="unsubscribe-card"
          >
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[color:var(--brand-primary)]" />
              <h1 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">
                Unsubscribe
              </h1>
            </div>

            {state.loading ? (
              <div
                className="mt-5 text-sm text-[color:var(--text-3)] inline-flex items-center gap-2"
                data-testid="unsubscribe-loading"
              >
                <Loader2 className="h-4 w-4 animate-spin" /> Checking your link…
              </div>
            ) : state.unsubscribed ? (
              <div className="mt-5" data-testid="unsubscribe-success">
                <div className="flex items-start gap-2 rounded-md border border-[color:rgba(34,197,94,0.30)] bg-[color:rgba(34,197,94,0.06)] p-3">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 text-[color:var(--success)] shrink-0" />
                  <div className="text-sm">
                    <div className="font-semibold text-[color:var(--text-1)]">
                      You&apos;re unsubscribed.
                    </div>
                    <div className="text-[color:var(--text-2)] mt-1 leading-relaxed">
                      You won&apos;t receive Postmoro marketing or
                      product-update emails anymore. Transactional account,
                      billing, security, and support emails may still be sent
                      when needed.
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-center text-xs text-[color:var(--text-3)]">
                  <span>Changed your mind?</span>
                  <Link
                    to="/login"
                    className="text-[color:var(--brand-primary)] hover:underline"
                    data-testid="unsubscribe-resubscribe-link"
                  >
                    Sign in to update preferences
                  </Link>
                </div>
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                <p className="text-sm text-[color:var(--text-2)] leading-relaxed">
                  You&apos;re about to unsubscribe from Postmoro marketing and
                  product-update emails. This will not affect transactional
                  emails (receipts, password reset, security notices, or
                  support replies).
                </p>
                {state.validToken && state.emailMasked ? (
                  <div
                    className="text-xs text-[color:var(--text-3)] font-mono"
                    data-testid="unsubscribe-email-masked"
                  >
                    Email: {state.emailMasked}
                  </div>
                ) : (
                  <div
                    className="text-xs text-[color:var(--text-3)]"
                    data-testid="unsubscribe-token-note"
                  >
                    We&apos;ll process this unsubscribe request. If your link
                    has expired we&apos;ll still confirm the change.
                  </div>
                )}
                {state.error ? (
                  <div
                    className="text-xs text-[color:var(--danger)]"
                    data-testid="unsubscribe-error"
                  >
                    {state.error}
                  </div>
                ) : null}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <Link to="/" data-testid="unsubscribe-cancel">
                    <Button
                      variant="outline"
                      className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                    >
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    disabled={state.submitting}
                    onClick={doUnsubscribe}
                    className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                    data-testid="unsubscribe-confirm"
                  >
                    {state.submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Unsubscribe
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>

        <footer className="border-t border-[color:var(--border-subtle)] py-6 text-center text-xs text-[color:var(--text-3)]">
          <div>© {new Date().getFullYear()} Postmoro LLC</div>
          <div className="mt-1">
            <Link to="/privacy" className="hover:text-[color:var(--text-1)] mx-2">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-[color:var(--text-1)] mx-2">
              Terms
            </Link>
            <a
              href="mailto:support@postmoro.com"
              className="hover:text-[color:var(--text-1)] mx-2"
            >
              Contact
            </a>
          </div>
        </footer>
      </div>
    </>
  );
}
